import { useCallback, useEffect, useState } from 'react'
import Sheet from '../components/Sheet.jsx'
import PushToggle from '../components/PushToggle.jsx'
import { addReminder, dismissReminder, getReminders } from '../lib/notes.js'
import ReminderSheet from '../components/ReminderSheet.jsx'
import AdaptiveListDetail from '../components/AdaptiveListDetail.jsx'

function when(iso) {
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  const time = d.toLocaleTimeString('en-PH', { hour: 'numeric', minute: '2-digit' })
  if (sameDay) return 'Today, ' + time
  return d.toLocaleDateString('en-PH', { day: 'numeric', month: 'short' }) + ', ' + time
}

export default function Reminders({ toast, refreshKey }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(false)
  const [openReminder, setOpenReminder] = useState(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setRows(await getReminders())
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [])

  useEffect(() => { load() }, [load, refreshKey])

  const newReminderAction = (
    <button type="button" className="btn ghost reminder-new" onClick={() => setOpen(true)}>
      New reminder
    </button>
  )

  return (
    <>
      <AdaptiveListDetail
        storageKey="mypersonalos.split.remindersWidth"
        listLabel="Reminders"
        selectedId={openReminder?.id ?? null}
        emptyLabel="Select a reminder"
        toolbarAction={newReminderAction}
        listFooter={<PushToggle toast={toast} />}
        list={(
          <>
            <div className="page-head">
              <span className="lbl">Reminders</span>
              <h1 className="serif">What's coming</h1>
            </div>

            {error ? <div className="notice warn"><p style={{ margin: 0, fontSize: 14 }}>{error}</p></div> : null}
            {!rows && !error ? <p className="lbl" style={{ padding: '20px 2px' }}>Loading…</p> : null}

            {rows && rows.length === 0 ? (
              <div className="empty">
                <h2 className="serif">Nothing set</h2>
                <p>A reminder can point at anything — a project, a board item, a payable.</p>
              </div>
            ) : null}

            {rows && rows.length ? (
              <div className="card">
                <div className="rows">
                  {rows.map((r) => (
                    <div className={'row reminder-row' + (openReminder?.id === r.id ? ' selected' : '')}
                      key={r.id} data-context-row="">
                      <button
                        type="button"
                        className="tick"
                        aria-label={'Dismiss: ' + r.body}
                        data-context-action="Dismiss"
                        onClick={async () => {
                          try {
                            await dismissReminder(r.id)
                            load()
                          } catch (e) {
                            toast(e.message || 'Could not dismiss that.')
                          }
                        }}
                      >
                        ✓
                      </button>
                      {/* The tick still closes it outright. Everything else about
                          the reminder lives one tap in. */}
                      <button
                        type="button"
                        className="rowopen"
                        data-detail-id={r.id}
                        aria-current={openReminder?.id === r.id ? 'true' : undefined}
                        onClick={() => setOpenReminder(r)}
                        aria-label={'Open reminder: ' + r.body}
                      >
                        <span className="nm">
                          <b style={{ whiteSpace: 'normal' }}>{r.body}</b>
                          <span className="lbl">{when(r.remind_at)}</span>
                          {r.note ? <span className="lbl">{r.note}</span> : null}
                        </span>
                        <span className="go" aria-hidden="true">›</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}
        detail={openReminder ? (
          <ReminderSheet
            key={openReminder.id}
            reminder={openReminder}
            onClose={() => setOpenReminder(null)}
            onChanged={load}
            toast={toast}
          />
        ) : null}
      />

      {open ? (
        <NewReminderSheet
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load() }}
          toast={toast}
        />
      ) : null}
    </>
  )
}

function NewReminderSheet({ onClose, onSaved, toast }) {
  const [body, setBody] = useState('')
  const [at, setAt] = useState(() => {
    const d = new Date(Date.now() + 3600000)
    d.setMinutes(0, 0, 0)
    /* datetime-local wants local time with no zone, so build it by hand
       rather than slicing toISOString(), which would shift it to UTC. */
    const p = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
  })
  const [busy, setBusy] = useState(false)

  return (
    <Sheet title="New reminder" onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !body.trim()) return
          setBusy(true)
          try {
            await addReminder({ body, remindAt: new Date(at).toISOString() })
            onSaved()
          } catch (err) {
            toast(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="rem-body">Remind me to</label>
          <input id="rem-body" value={body} onChange={(e) => setBody(e.target.value)} required />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="rem-at">When</label>
          <input id="rem-at" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} required />
        </div>
        <button className="btn" type="submit" data-sheet-primary disabled={busy || !body.trim()}>
          {busy ? 'Saving…' : 'Set reminder'}
        </button>
      </form>
    </Sheet>
  )
}
